import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import type { ItemWithPrice } from "@/modules/pricing/resourcePricing.ts";
import { bucketLink } from "@/modules/storage/links.ts";
import { jsonEncodedBody, urlEncodedBody } from "@/utils/fetchClient.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { produceQueryData } from "@/utils/query/produceQueryData.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import type { QueryClient } from "@tanstack/query-core";
import { type QueryKey, queryOptions } from "@tanstack/react-query";
import type { Bucket, BucketCreateBody, BucketUpdateBody, S3Url } from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries } from "@warrenio/utils/collections/maps";

//#region Types/Contracts
export interface Params {
    location: string;
}

export interface BucketWithType extends Bucket {
    $type: "bucket";
}

export interface BucketWithPrice extends BucketWithType, ItemWithPrice {}

type Response = Map<Bucket["name"], BucketWithType>;
const emptyResponse: Response = new Map();

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("bucket") };

//#endregion

//#region helper functions

export function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["storage/bucket/list"] : ["storage/bucket/list", params.location];
}

function bucketFromData(data: Bucket): BucketWithType {
    return { ...data, $type: "bucket" };
}

//#endregion helper functions

//#region Queries

/** @returns Map of {@link Bucket}s keyed by name */
export function bucketListQuery(apiClient: ApiClient, params?: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }) => {
            const response = getResponseData(await apiClient.GET("/storage/bucket/list", { signal }));

            return mapFromEntries(response, (o) => [o.name, bucketFromData(o)]);
        },
    });
}

export function getS3ApiUrlQuery(apiClient: ApiClient) {
    return queryOptions({
        queryKey: ["s3_url"],
        queryFn: async ({ signal }): Promise<S3Url> =>
            getResponseData(await apiClient.GET("/storage/api/s3", { signal })),
    });
}
//#endregion Queries

//#region Mutations

export function createBucketMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/bucket/create"],
        async mutationFn({ body }: { body: BucketCreateBody }) {
            const result = getResponseData(
                await apiClient.POST("/storage/bucket", {
                    ...jsonEncodedBody,
                    body,
                }),
            );
            return bucketFromData(result);
        },
        onSuccess(res) {
            produceQueryData(queryClient, getQueryKey(), emptyResponse, (data) => {
                data.set(res.name, res);
            });
        },
        onSettled(res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage bucket created",
                error: "Failed to create storage bucket",
                successType: "success",
                viewLink: res ? bucketLink(res) : undefined,
            });

            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey() }));
        },
    });
}

export function deleteBucketMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/bucket/delete"],
        async mutationFn({ name }: { name: string }) {
            return getResponseData(
                await apiClient.DELETE("/storage/bucket", {
                    ...urlEncodedBody,
                    body: { name },
                }),
            );
        },

        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage bucket deleted",
                error: "Failed to delete storage bucket",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

export function changeBucketMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/bucket/update"],
        async mutationFn({ body }: { body: BucketUpdateBody }) {
            return getResponseData(
                await apiClient.PATCH("/storage/bucket", {
                    ...urlEncodedBody,
                    body,
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage bucket updated",
                error: "Failed to update storage bucket",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

//#endregion Mutations
